<template>
  <b-card no-body header-tag="header" footer-tag="footer">
    <template slot="header">
      <div class="total-count">
        {{ totalRows }}
        <span class="label">Samples found</span>
      </div>
      <!-- Search Box -->
      <div class="search-box" :class="{active: filter}">
        <div class="input-group">
          <div class="input-group-prepend">
            <div class="input-group-text">
              <font-awesome-icon icon="search" fixed-width class="icon"/>
            </div>
          </div>
          <b-form-input type="text" v-model="filter" placeholder="Find a sample by keyword..."/>
          <button class="clear-search" @click="clearSearch()">
            <font-awesome-icon icon="times-circle" fixed-width class="icon"/>
          </button>
        </div>
      </div>
    </template>

    <b-card-body>
      <!-- Per page selection -->
      <div class="form-inline per-page-selection d-flex align-items-center float-left">
        <b-form-select :options="pageOptions" v-model="perPage" @change="changePageSize"/>
        <span class="ml-2 per-page-label">Samples Per Page</span>
      </div>
      <div class="float-right">
        <button class="btn btn-sm btn-outline-secondary" v-b-modal.selectSamplesColumnModal>
          <font-awesome-icon icon="cog" class="icon"/>Column Settings
        </button>
        <button
          class="btn btn-sm btn-outline-secondary ml-2"
          v-if="totalRows < 1000 && totalRows > 0"
          @click="csvDownload"
        >
          <font-awesome-icon icon="file-export" class="icon" v-if="!downloading"/>
          <font-awesome-icon icon="spinner" class="icon" spin v-if="downloading"/>Export CSV
        </button>
      </div>
      <!-- Column Selection Modal -->
      <b-modal
        ref="selectSamplesColumnModal"
        id="selectSamplesColumnModal"
        size="sm"
        centered
        title="Column Settings"
        button-size="sm"
        cancel-variant="outline-secondary"
      >
        <p class="help-text">Select which table columns you want displayed.</p>
        <b-form-checkbox-group stacked v-model="selectedCheckboxes">
          <b-form-checkbox
            v-for="field in fields"
            :key="field.key"
            name="columnselect"
            :value="field.key"
          >{{ field.label }}</b-form-checkbox>
        </b-form-checkbox-group>
        <template slot="modal-footer">
          <button class="btn btn-primary" @click="applyColumnSettings">Save Settings</button>
          <button class="btn btn-outline-secondary" @click="closeModal">Cancel</button>
        </template>
      </b-modal>
    </b-card-body>

    <!-- Samples Table -->
    <b-table
      ref="samplesTable"
      show-empty
      striped
      responsive
      :busy.sync="loading"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :items="doSamplesSearch"
      :fields="fields"
      :current-page="currentPage"
      :per-page="perPage"
      :filter="filter"
      @filtered="onFiltered"
      empty-text="There are no samples to display"
    >
      <div slot="table-busy" class="text-center text-danger my-2">
        <b-spinner class="align-middle" variant="primary"/>
      </div>
      <!-- Filter Row -->
      <template slot="top-row" slot-scope="data">
        <td v-for="field in data.fields" :key="field.filterKey" :class="field.tdClass">
          <input
            type="text"
            @keyup="updateColumnFilters()"
            class="form-control form-control-sm"
            v-if="field.filterType == 'text'"
            v-model="filters[field.filterKey]"
          >
          <input
            type="date"
            class="form-control form-control-sm"
            @change="updateColumnFilters()"
            v-if="field.filterType == 'date'"
            v-model="filters[field.filterKey]"
          >

          <input
            type="date"
            class="form-control form-control-sm"
            @change="updateColumnFilters()"
            v-if="field.filterType == 'date-range'"
            v-model="filters[field.filterKey][0]"
            style="width: 90%;"
          >
          <input
            type="date"
            class="form-control form-control-sm"
            @change="updateColumnFilters()"
            v-if="field.filterType == 'date-range'"
            v-model="filters[field.filterKey][1]"
            style="width: 90%;"
          >
        </td>
      </template>

      <!-- Lab Ref -->
      <template slot="labSampleReference" slot-scope="data">
        <router-link tag="a" :to="`/jobs/${ data.item.job.jobId }`" v-if="jobId === 'all'">
          <strong>
            {{ data.item.labSampleReference }}
            <font-awesome-icon icon="angle-right" fixed-width class="icon"/>
          </strong>
        </router-link>
        <!-- Dont link if on Job Detail page -->
        <span v-else>{{ data.item.labSampleReference }}</span>
      </template>

      <!-- Sample Ref -->
      <template slot="clientSampleReference" slot-scope="data">
        <router-link tag="a" :to="`/jobs/${ data.item.job.jobId }/samples/${data.item.sampleId}`">
          <strong>
            {{ data.item.clientSampleReference }}
            <font-awesome-icon icon="angle-right" fixed-width class="icon"/>
          </strong>
        </router-link>
      </template>

      <!-- Registered Date -->
      <template slot="registeredDateTime" slot-scope="data">
        <span
          v-if="data.item.job.registeredDateTime != null"
        >{{ data.item.job.registeredDateTime | moment("D MMM YYYY") }}</span>
      </template>

      <!-- Sample Date -->
      <template slot="sampleDate" slot-scope="data">
        <span v-if="data.item.sampleDate != null">{{ data.item.sampleDate | moment("D MMM YYYY") }}</span>
      </template>
    </b-table>

    <!-- Footer -->
    <template slot="footer">
      <div class="d-flex align-items-center justify-content-between">
        <span>Page {{ currentPage }} of {{ totalPages }}</span>
        <b-pagination :total-rows="totalRows" :per-page="perPage" v-model="currentPage"/>
      </div>
    </template>
  </b-card>
</template>

<style lang="scss">
@import "../assets/styles/main.scss";
</style>

<script>
import { searchSamples } from "@/services/api";
import { EventBus } from "@/services/event-bus.js";
import {
  storeSampleFields,
  getSampleFields,
  getSampleSort,
  storeSampleSort
} from "@/services/localstorage";
const moment = require("moment");
const Json2csvParser = require("json2csv").Parser;

export default {
  name: "SamplesTable",
  components: {},
  props: {
    jobId: {
      type: String,
      default: () => {
        return "all";
      }
    },
    selectedClients: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // add default visible fields/column keys here
      selectedCheckboxes: [
        "clientSampleReference",
        "labSampleReference",
        "matrix",
        "registeredDateTime",
        "sampleDate"
      ],
      loading: false,
      currentPage: 1,
      perPage: 20,
      totalRows: 0,
      pageOptions: [20, 50, 100, 500],
      filter: null,
      jobFilled: false,
      totalPages: 0,
      searchactive: false,
      sortBy: "labSampleReference",
      sortDesc: true,
      downloading: false,
      filters: {
        labSampleReference: "",
        registrationDescription: "",
        clientSampleReference: "",
        matrix: "",
        registeredDateTime: ["", ""],
        sampleDate: ["", ""]
      },
      timeout: null,
      fields: [
        {
          key: "labSampleReference",
          label: "Lab Reference",
          sortable: true,
          thClass: "",
          tdClass: "",
          filterType: "text",
          filterKey: "labSampleReference"
        },
        {
          key: "job.registrationDescription",
          label: "Job Description",
          sortable: true,
          thClass: "",
          tdClass: "",
          filterType: "text",
          filterKey: "job_registrationDescription"
        },
        {
          key: "clientSampleReference",
          label: "Sample Reference",
          sortable: true,
          thClass: "",
          tdClass: "",
          filterType: "text",
          filterKey: "clientSampleReference"
        },
        {
          key: "matrix",
          label: "Matrix",
          sortable: true,
          thClass: "",
          tdClass: "",
          filterType: "text",
          filterKey: "matrix"
        },
        {
          key: "registeredDateTime",
          label: "Received",
          sortable: true,
          thClass: "",
          tdClass: "",
          filterType: "date-range",
          filterKey: "registeredDateTime"
        },
        {
          key: "sampleDate",
          label: "Sample Date",
          sortable: true,
          thClass: "",
          tdClass: "",
          filterType: "date-range",
          filterKey: "sampleDate"
        },
        // these are all optional fields - hidden by default
        {
          key: "samplingDepth",
          label: "Sampling Depth",
          sortable: true,
          thClass: "d-none",
          tdClass: "d-none"
        },
        {
          key: "samplesInComposite",
          label: "Samples In Composite",
          sortable: true,
          thClass: "d-none",
          tdClass: "d-none"
        },
        {
          key: "batchReference",
          label: "Batch Reference",
          sortable: true,
          thClass: "d-none",
          tdClass: "d-none"
        },
        {
          key: "cowNumber",
          label: "Cow Number",
          sortable: true,
          thClass: "d-none",
          tdClass: "d-none"
        },
        {
          key: "factoryId",
          label: "Factory Id",
          sortable: true,
          thClass: "d-none",
          tdClass: "d-none"
        }
      ]
    };
  },
  watch: {
    loading: function() {
      if (this.loading) {
        EventBus.$emit("showLoader");
      } else {
        EventBus.$emit("hideLoader");
      }
    },
    selectedClients: function() {
      this.refreshTable();
    },
    selectedCheckboxes: function(newSelectedCheckboxes) {
      storeSampleFields(newSelectedCheckboxes);
    }
  },
  methods: {
    updateColumnFilters() {
      this.currentPage = 1;
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.refreshTable();
      }, 500);
    },
    applyColumnSettings() {
      this.$refs.selectSamplesColumnModal.hide();
      this.fields.forEach(field => {
        if (this.selectedCheckboxes.indexOf(field.key) > -1) {
          // show it
          this.toggleColumnVisibility(field, true);
        } else {
          // hide it
          this.toggleColumnVisibility(field, false);
        }
      });
    },
    // hide of show a table column
    toggleColumnVisibility(column, displayFlag) {
      if (displayFlag) {
        column.thClass = "";
        column.tdClass = "";
      } else {
        // hide it
        column.thClass = "d-none";
        column.tdClass = "d-none";
      }
    },
    refreshTable() {
      //if (this.$refs.samplesTable && /^\/(samples)/.test(this.$root._route.fullPath))
      this.$refs.samplesTable.refresh();
    },
    makeParams() {
      const params = {
        page: this.currentPage,
        pageSize: this.perPage,
        orderFields: [
          {
            field: this.sortBy.replace(".", "_"),
            direction: this.sortDesc ? "DESC" : "ASC"
          }
        ],
        searchFields: {}
      };

      // Override the sorting if it's on the labSampleReference column
      if (this.sortBy === 'labSampleReference') {
        params.orderFields = [
          {
            field: 'job_labRegistrationReference',
            direction: this.sortDesc ? "DESC" : "ASC"
          },
          {
            field: 'sampleNumber',
            direction: this.sortDesc ? "DESC" : "ASC"
          }
        ];
      }

      if (this.filter && this.filter.trim())
        params.textSearch = this.filter.trim();

      // Column Filters
      this.fields.forEach(field => {
        let key = field.filterKey;
        if (field.filterType == "text") {
          if (this.filters[key] && this.filters[key].trim())
            params.searchFields[key] = this.filters[key].trim();
        } else if (field.filterType == "date") {
          if (this.filters[key])
            params.searchFields[key] = [
              {
                value: this.filters[key],
                operator: "="
              }
            ];
        } else if (
          field.filterType === "date-range" ||
          field.filterType === "number"
        ) {
          if (this.filters[key][0]) {
            params.searchFields[key] = [
              {
                value: this.filters[key][0],
                operator: ">="
              }
            ];
          }
          if (this.filters[key][1]) {
            if (!params.searchFields[key]) params.searchFields[key] = [];
            params.searchFields[key].push({
              value: this.filters[key][1],
              operator: "<="
            });
            // we need to track where this came from, so
            if (params.searchFields[key].length === 1) {
              params.searchFields[key][0].isFirst = true;
            }
          }
        }
      });

      if (this.jobId === "all" && this.selectedClients.length > 0) {
        params.searchFields.clients = this.selectedClients.map(
          client => client.id
        );
      }
      if (this.jobId !== "all") {
        params.searchFields.jobId = this.jobId;
      }
      return params;
    },
    restoreParams(config) {
      // always setting this to one - pagination won't set it properly for some reason.
      this.currentPage = 1; //config.page || 1;
      this.perPage = config.pageSize || 20;
      this.filter = config.textSearch || "";

      if (config.orderFields && config.orderFields.length > 0) {
        if (config.orderFields[0].field) {
          this.sortBy = config.orderFields[0].field.replace("_", ".");
          this.sortDesc = true;
          if (config.orderFields[0].direction !== "DESC") {
            this.sortDesc = false;
          }
        }
      }

      // now the column filters - a wee bit trickier
      if (config.searchFields) {
        const keys = Object.keys(config.searchFields);
        keys.forEach(key => {
          // find the object in the fields array
          let field = null;
          this.fields.forEach(f => {
            if (f.filterKey === key) {
              field = f;
            }
          });

          if (field) {
            if (field.filterType) {
              switch (field.filterType) {
              case "number":
              case "date-range":
                if (config.searchFields[key].length > 0) {
                  this.filters[key][0] = config.searchFields[key][0].value;

                  if (config.searchFields[key][0].isFirst) {
                    this.filters[key][0] = "";
                    this.filters[key][1] = config.searchFields[key][0].value;
                    break;
                  }
                } else {
                  this.filters[key][0] = "";
                }

                if (config.searchFields[key].length > 1) {
                  this.filters[key][1] = config.searchFields[key][1].value;
                } else {
                  this.filters[key][1] = "";
                }
                break;
              case "date":
                this.filters[key] = config.searchFields[key].value;
                break;
              case "select":
              case "text":
              default:
                this.filters[key] = config.searchFields[key];
                break;
              }
            } else {
              this.filters[key] = config.searchFields[key];
            }
          }
        });
      }
    },
    filterMarkup(params) {
      // Have to add some marker fields to the params in order to be able to restore them,
      // and this then removes those fields so that the search does not fail.
      this.fields.forEach(field => {
        let key = field.filterKey;
        if (Array.isArray(params.searchFields[key])) {
          if (params.searchFields[key].length > 0) {
            for (let i = 0; i < params.searchFields[key].length; i++) {
              let p = params.searchFields[key][i];
              if (p.isFirst) {
                delete p.isFirst;
              }
            }
          }
        }
      });

      return params;
    },
    async doSamplesSearch(ctx) {
      let params = this.makeParams();
      storeSampleSort(params);
      params = this.filterMarkup(params);

      const result = await searchSamples(params);
      this.perPage = result.pagination.pageSize;
      this.totalRows = result.pagination.totalResults;
      this.totalPages = Math.ceil(this.totalRows / this.perPage);
      console.log(result.samples);
      return result.samples;
    },
    forceFileDownload(response, filename) {
      const blob = new Blob([response]);

      //for microsoft IE
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, filename);
      } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        // no filename in response :(
        link.setAttribute("download", filename); // have to specify a filename here
        document.body.appendChild(link);
        this.downloading = false;
        link.click();
      }
    },
    async csvDownload() {
      this.downloading = true;
      const promises = [];
      const paramsTemplate = this.makeParams();
      const pages = Math.ceil(this.totalRows / 500);
      for (let i = 0; i <= pages; i++) {
        const params = JSON.parse(JSON.stringify(paramsTemplate));
        params.page = i + 1;
        params.pageSize = 500;
        promises.push(searchSamples(params));
      }
      const results = await Promise.all(promises);
      const rows = results.reduce((accumulator, resultSet) => {
        resultSet.samples.forEach(sample => {
          Object.keys(sample.job).forEach(jobKey => {
            sample[`job.${jobKey}`] = sample.job[jobKey];
          });
          delete sample.job;
          accumulator.push(sample);
        });
        return accumulator;
      }, []);
      const fields = Object.keys(rows[0]);
      try {
        const opts = { fields };
        const parser = new Json2csvParser(opts);
        const csv = parser.parse(rows);
        this.forceFileDownload(
          csv,
          `analytica_samples_search_exported_${moment().format(
            "YYYY_MM_DD_HH_mm_ss"
          )}.csv`
        );
      } catch (err) {
        console.error(err);
      }

      this.downloading = false;
    },
    closeModal() {
      this.$refs.selectSamplesColumnModal.hide();
    },
    onFiltered() {},
    clearSearch() {
      this.filter = "";
      this.refreshTable();
    },
    changePageSize(value) {
      this.currentPage = 1;
      this.perPage = value;
      this.refreshTable();
    }
  },
  mounted() {
    const storedSampleFields = getSampleFields();
    this.selectedCheckboxes = storedSampleFields || this.selectedCheckboxes;
    if (this.jobId === "all") {
      if (!storedSampleFields) {
        this.selectedCheckboxes.push("job.registrationDescription");
      }
    }

    const sortConfig = getSampleSort();
    if (sortConfig) {
      this.restoreParams(sortConfig);
    }

    this.refreshTable();
    this.applyColumnSettings();
  }
};
</script>



// WEBPACK FOOTER //
// src/components/SamplesTable.vue